// src/components/PurpleLine.jsx
import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import { PURPLE_STATIONS } from "../stations/purpleStations";

export default function PurpleLine({
  map,
  theme = "light",
  showStations = true,
  fitOnMount = false,
}) {
  const popupRef = useRef(null);
  const didFitRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    const isDark = theme === "dark";

    const stations = (PURPLE_STATIONS || []).filter(
      (s) => typeof s.lat === "number" && typeof s.lng === "number"
    );
    const points = stations.map(({ lat, lng }) => [lng, lat]);
    if (points.length < 2) return;

    const removeLayersAndSources = () => {
      ["purple-line-layer", "purple-stations-layer", "purple-stations-labels"].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      ["purple-line", "purple-stations"].forEach((id) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };

    const ensureOrder = () => {
      if (map.getLayer("place-major")) {
        ["purple-stations-labels", "purple-stations-layer", "purple-line-layer"].forEach((id) => {
          if (map.getLayer(id)) {
            try {
              map.moveLayer(id, "place-major");
            } catch {
              /* no-op */
            }
          }
        });
      }
    };

    const addLayers = () => {
      if (!map.isStyleLoaded()) return;
      removeLayersAndSources();

      // Fit map to line bounds if requested
      if (fitOnMount && !didFitRef.current) {
        const bounds = points.reduce(
          (acc, p) => acc.extend(p),
          new maplibregl.LngLatBounds(points[0], points[0])
        );
        map.fitBounds(bounds, { padding: 40, duration: 300 });
        didFitRef.current = true;
      }

      // Line Layer
      map.addSource("purple-line", {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "LineString", coordinates: points } },
      });
      map.addLayer({
        id: "purple-line-layer",
        type: "line",
        source: "purple-line",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#7C3AED", "line-width": 4, "line-opacity": 0.95 },
      });

      // Station Layer + Popup
      if (showStations) {
        map.addSource("purple-stations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: stations.map((s) => ({
              type: "Feature",
              properties: { name: s.name },
              geometry: { type: "Point", coordinates: [s.lng, s.lat] },
            })),
          },
        });

        map.addLayer({
          id: "purple-stations-layer",
          type: "circle",
          source: "purple-stations",
          paint: {
            "circle-radius": 8,
            "circle-color": "#7C3AED",
            "circle-stroke-color": isDark ? "#000" : "#fff",
            "circle-stroke-width": 2.5,
            "circle-opacity": 1,
          },
        });

        // Popup
        map.on("click", "purple-stations-layer", (e) => {
          const feature = e.features?.[0];
          if (!feature) return;
          const coordinates = feature.geometry.coordinates.slice();
          const stationName = feature.properties.name;

          if (popupRef.current) popupRef.current.remove();

          const rect = map.getCanvas().getBoundingClientRect();
          const x = e.point.x;
          const y = e.point.y;

          let anchor = "bottom";
          if (y < rect.height * 0.3) anchor = "top";
          else if (y > rect.height * 0.7) anchor = "bottom";
          if (x < rect.width * 0.3) anchor += "-left";
          else if (x > rect.width * 0.7) anchor += "-right";

          const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 25,
            anchor,
            maxWidth: "none",
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div class="custom-popup-card" style="
                font-family: Inter, sans-serif;
                background: ${isDark ? "#1f1f1f" : "#ffffff"};
                color: ${isDark ? "#f9f9f9" : "#111"};
                border-radius: 14px;
                box-shadow: 0 6px 18px rgba(0,0,0,0.25);
                border: ${isDark ? "1px solid #333" : "1px solid #ddd"};
                max-width: 240px;
                width: 80vw;
                padding: 16px 18px;
              ">
                <div style="font-weight:700;font-size:15px;margin-bottom:10px;">${stationName}</div>
                <div style="font-size:13px;opacity:0.8;">Metro Station</div>
              </div>
            `)
            .addTo(map);

          const popupEl = popup.getElement();
          const contentEl = popupEl.querySelector(".maplibregl-popup-content");
          if (contentEl) contentEl.replaceWith(...contentEl.childNodes);
          popupRef.current = popup;
        });

        // Close popup on outside click
        map.on("click", (e) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ["purple-stations-layer"] });
          if (!features.length && popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
          }
        });
      }

      ensureOrder();
    };

    const onLoad = () => addLayers();
    const onIdle = () => ensureOrder();

    if (map.isStyleLoaded()) addLayers();
    map.on("load", onLoad);
    map.once("style.load", () => setTimeout(() => addLayers(), 300));
    map.on("idle", onIdle);

    return () => {
      if (popupRef.current) popupRef.current.remove();
      map.off("load", onLoad);
      map.off("idle", onIdle);
      if (map.isStyleLoaded()) removeLayersAndSources();
    };
  }, [map, theme, showStations, fitOnMount]);

  return null;
}
